import {
  IRateResSchema,
  OfferingTypesEnum,
  RateBillingFrequencyEnum,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import {
  MAccordionCustomButton,
  MAccordionCustomButtonItem,
  MAlert,
  MBox,
  MFlex,
  MGrid,
  MIDCopyBox,
  MTag,
  MText,
} from '../../../../../components/Monetize';
import { RATE_BILLING_FREQUENCY_MAP } from '../../../../../constants/offerings';
import LockedStatus from './LockedStatus';
import { RateActions } from './RateActions';

const gridTemplateCollapsed = '1fr';

interface RateFormCollapsedProps {
  name: string;
  currency: string;
  billingFrequency: Maybe<RateBillingFrequencyEnum>;
  billingFrequencyInMonths: Maybe<number>;
  type: OfferingTypesEnum;
  isRateInactive: boolean;
  showUnsavedAlert: boolean;
  isLocked: boolean;
  isReadOnly?: boolean;
  id: string;
  offeringRate: IRateResSchema | null;
  handleClone: () => void;
  handleRemove: () => Promise<void>;
  handleStatusChange: (status: any) => Promise<void>;
}

export const RateFormCollapsed = ({
  name,
  currency,
  billingFrequency,
  billingFrequencyInMonths,
  type,
  isRateInactive,
  showUnsavedAlert,
  isLocked,
  isReadOnly,
  id,
  offeringRate,
  handleClone,
  handleRemove,
  handleStatusChange,
}: RateFormCollapsedProps) => {
  return (
    <MGrid
      gridTemplateColumns={gridTemplateCollapsed}
      gap={0}
      py={1}
      pl={2}
      pr={0}
    >
      <MAccordionCustomButton
        isExpanded={false}
        label={name}
        customLabel={
          <MBox>
            <MFlex alignItems="center">
              <MText fontWeight="600" fontSize="lg" ml={1} lineClamp={2}>
                {name}
              </MText>
              {isRateInactive && (
                <MTag ml={2} px={2} py={1} h={5}>
                  Inactive
                </MTag>
              )}
            </MFlex>
            {id?.startsWith('rate_') && <MIDCopyBox copyValue={id} />}
          </MBox>
        }
        buttonProps={{
          pr: 3,
          _hover: {
            bg: 'tWhite.base',
          },
        }}
        testId="toggle-rate-button"
      >
        <MFlex justify="space-between">
          {showUnsavedAlert && (
            <MAlert
              type="warning"
              alertProps={{ px: 2, py: 1 }}
              alertIconProps={{ width: 4, height: 4 }}
              alertDescriptionProps={{ fontSize: 'sm' }}
              alignItems="center"
            >
              Unsaved Changes
            </MAlert>
          )}
          <MFlex gap={2}>
            <MAccordionCustomButtonItem label="Currency" value={currency} />
            <MAccordionCustomButtonItem
              label={
                type === OfferingTypesEnum.MIN_COMMIT
                  ? 'Minimum Commit Frequency'
                  : 'Subscription Frequency'
              }
              labelTextProps={{ lineClamp: 2 }}
              value={
                billingFrequency
                  ? RATE_BILLING_FREQUENCY_MAP[billingFrequency](
                      billingFrequencyInMonths,
                    ).label
                  : ''
              }
            />
          </MFlex>
          <MFlex alignItems="center">
            {isLocked && (
              <LockedStatus
                containerProps={{
                  ml: 4,
                }}
              />
            )}
            {!isReadOnly && (
              <MFlex
                ml={isLocked ? 0 : 4}
                onClick={(ev) => ev.stopPropagation()}
              >
                <RateActions
                  isLocked={isLocked}
                  isNewRate={id?.match('new-') !== null}
                  rateId={offeringRate?.id || ''}
                  handleClone={handleClone}
                  handleRemove={handleRemove}
                  rateStatus={offeringRate?.status}
                  handleStatusChange={handleStatusChange}
                />
              </MFlex>
            )}
          </MFlex>
        </MFlex>
      </MAccordionCustomButton>
    </MGrid>
  );
};
